import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Share2,
  Copy,
  Check,
  RefreshCw,
  Ban,
  Link2,
  Eye,
  Loader2,
  Mail,
  MessageSquare,
  Users,
  Lock,
} from "lucide-react";

const publicUrl = (token) => `${window.location.origin}/brief/${token}`;

export function ShareDialog({ decisionId, title, buttonSize }) {
  const { t } = useTranslation();
  const briefTitle = title || "ORKEVA Decision Brief";
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expiry, setExpiry] = useState("0"); // days as string; "0" = never

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get(`/shares/decision/${decisionId}`)
      .then((r) => setShare(r.data))
      .catch(() => setShare({ active: false }))
      .finally(() => setLoading(false));
  }, [open, decisionId]);

  const doCopy = async (token, silent = false) => {
    try {
      await navigator.clipboard.writeText(publicUrl(token));
      if (!silent) {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
      return true;
    } catch {
      if (!silent) toast.error(t("share.errCopy"));
      return false;
    }
  };

  // Two-click flow: open dialog → "Create & copy".
  const createAndCopy = async () => {
    setBusy("create");
    try {
      const r = await api.post(`/shares/decision/${decisionId}`, {
        expires_in_days: Number(expiry) || null,
      });
      setShare(r.data);
      await doCopy(r.data.token, true);
      toast.success(t("share.createdCopied"));
    } catch {
      toast.error(t("share.errCreate"));
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async () => {
    setBusy("regen");
    try {
      const r = await api.post(`/shares/decision/${decisionId}/regenerate`, {
        expires_in_days: Number(expiry) || null,
      });
      setShare(r.data);
      toast.success(t("share.regenerated"));
    } catch {
      toast.error(t("share.errCreate"));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async () => {
    setBusy("revoke");
    try {
      await api.post(`/shares/decision/${decisionId}/revoke`);
      setShare({ active: false });
      toast.success(t("share.revoked"));
    } catch {
      toast.error(t("share.errRevoke"));
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    if (await doCopy(share.token)) toast.success(t("share.copied"));
  };

  const shareVia = (target) => {
    const url = publicUrl(share.token);
    const subject = t("share.msgSubject", { title: briefTitle });
    const text = t("share.msgText", { title: briefTitle, url });
    if (target === "email") {
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    } else if (target === "teams") {
      window.open(
        `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(subject)}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else if (target === "slack") {
      doCopy(share.token, true);
      window.open("https://slack.com/", "_blank", "noopener,noreferrer");
      toast.success(t("share.slackHint"));
    }
  };

  const active = share?.active && share?.token;
  const expiryLabel = share?.expires_at
    ? t("share.expiresOn", { date: new Date(share.expires_at).toLocaleDateString() })
    : t("share.neverExpires");

  const ExpirySelect = (
    <Select value={expiry} onValueChange={setExpiry}>
      <SelectTrigger className="w-full" data-testid="share-expiry-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="0">{t("share.expiry.never")}</SelectItem>
        <SelectItem value="1">{t("share.expiry.d1")}</SelectItem>
        <SelectItem value="7">{t("share.expiry.d7")}</SelectItem>
        <SelectItem value="30">{t("share.expiry.d30")}</SelectItem>
      </SelectContent>
    </Select>
  );

  const QuickActions = (
    <div className="grid grid-cols-3 gap-2" data-testid="share-quick-actions">
      <QuickBtn icon={Mail} label={t("share.email")} onClick={() => shareVia("email")} testId="share-email-btn" />
      <QuickBtn icon={MessageSquare} label="Slack" onClick={() => shareVia("slack")} testId="share-slack-btn" />
      <QuickBtn icon={Users} label="Teams" onClick={() => shareVia("teams")} testId="share-teams-btn" />
    </div>
  );

  return (
    <>
      <Button
        variant="outline"
        size={buttonSize || "default"}
        className="border-border"
        onClick={() => setOpen(true)}
        data-testid="share-open-btn"
      >
        <Share2 className="mr-1.5 h-4 w-4" /> {t("share.button")}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Link2 className="h-4 w-4 text-primary" /> {t("share.title")}
            </DialogTitle>
            <DialogDescription>{t("share.subtitle")}</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : active ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={publicUrl(share.token)}
                  className="font-mono text-xs"
                  data-testid="share-link-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  size="icon"
                  className="shrink-0 bg-primary text-primary-foreground hover:opacity-90"
                  onClick={copy}
                  data-testid="share-copy-btn"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>

              {QuickActions}

              <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5" />
                  {t("share.views", { count: share.view_count ?? 0 })}
                </span>
                <span className="font-medium">{expiryLabel}</span>
              </div>

              <p className="text-xs leading-relaxed text-muted-foreground">{t("share.readonlyNote")}</p>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-border"
                  onClick={regenerate}
                  disabled={busy}
                  data-testid="share-regenerate-btn"
                >
                  <RefreshCw className="mr-1.5 h-4 w-4" /> {t("share.regenerate")}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-danger/40 text-danger hover:bg-danger/5"
                  onClick={revoke}
                  disabled={busy}
                  data-testid="share-revoke-btn"
                >
                  <Ban className="mr-1.5 h-4 w-4" /> {t("share.revoke")}
                </Button>
              </div>

              {/* Enterprise controls (future) — visible, disabled affordances */}
              <div className="border-t border-border pt-3">
                <button
                  type="button"
                  disabled
                  className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-xs text-muted-foreground/60"
                  data-testid="share-password-soon"
                >
                  <Lock className="h-3.5 w-3.5" />
                  {t("share.passwordSoon")}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("share.createPrompt")}</p>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  {t("share.expiryLabel")}
                </label>
                {ExpirySelect}
              </div>
              <Button
                className="w-full bg-primary text-primary-foreground hover:opacity-90"
                onClick={createAndCopy}
                disabled={busy}
                data-testid="share-create-btn"
              >
                {busy === "create" ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Copy className="mr-1.5 h-4 w-4" />
                )}
                {t("share.createCopy")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function QuickBtn({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex flex-col items-center gap-1.5 rounded-lg border border-border bg-card px-2 py-3 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
